import { useState } from 'react';

import { ArrowLeft, Trash2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { useAuthStore } from '@/features/auth/auth-store';

import { CampaignOutcomeTable } from './CampaignOutcomeTable';
import { CampaignSummaryBar } from './CampaignSummaryBar';
import { DeleteCampaignDialog } from './DeleteCampaignDialog';
import { useCampaign, useUpdateOutcome } from './useCampaign';

/**
 * Campaign outcome-tracking detail view (SCRUM-72 + 73 + 74): summary bar over the outcome
 * table for one campaign, shown in the Campaigns page content area when a campaign is selected.
 * Data comes from GET /api/campaigns/{id}; status tagging goes through the optimistic
 * updateOutcome mutation, so the table row AND the derived summary bar update together.
 */
export function CampaignDetailView({
  campaignId,
  onBack,
}: {
  campaignId: string;
  onBack: () => void;
}) {
  const { data, isLoading, isError, refetch } = useCampaign(campaignId);
  const updateOutcome = useUpdateOutcome(campaignId);
  // Bank-Executive-only delete (SCRUM-813). This is CLIENT PARITY with the server's
  // Executive-only CampaignAdmin policy — the server is the authority; hiding the button just
  // keeps the UI honest for non-Executive roles (Officer/Manager/Teller never see it).
  const role = useAuthStore((s) => s.user?.role);
  const canDelete = role === 'Bank Executive';
  const [deleteOpen, setDeleteOpen] = useState(false);

  return (
    <div className="flex w-full flex-col gap-3">
      <button
        type="button"
        onClick={onBack}
        className="text-text-2 hover:text-text-0 inline-flex w-fit items-center gap-1.5 text-[0.8125rem] font-medium transition-colors"
      >
        <ArrowLeft className="h-3.5 w-3.5" />
        Back to customers
      </button>

      {isLoading && (
        <div className="border-border-1 bg-bg-3 rounded-tk-md text-text-3 border px-5 py-8 text-center text-sm">
          Loading campaign…
        </div>
      )}

      {isError && (
        <div className="border-border-1 bg-bg-3 rounded-tk-md flex flex-col items-center gap-2 border px-5 py-8 text-center text-sm">
          <span className="text-text-1">This campaign could not be loaded.</span>
          <button
            type="button"
            onClick={() => void refetch()}
            className="text-brand-primary text-[0.8125rem] font-medium hover:underline"
          >
            Try again
          </button>
        </div>
      )}

      {data && (
        <>
          <div className="flex items-start justify-between gap-3">
            <div className="flex flex-col gap-0.5">
              <h2 className="text-text-0 text-lg font-semibold">{data.name}</h2>
              <p className="text-text-3 text-xs">
                Outcome tracking · the officer&apos;s working list
              </p>
            </div>
            {canDelete && (
              <Button
                variant="destructive"
                size="sm"
                onClick={() => {
                  setDeleteOpen(true);
                }}
                data-icon="inline-start"
              >
                <Trash2 aria-hidden="true" />
                Delete
              </Button>
            )}
          </div>
          {canDelete && deleteOpen && (
            <DeleteCampaignDialog
              campaignId={campaignId}
              campaignName={data.name}
              outcomeCount={data.outcomes.length}
              onClose={() => {
                setDeleteOpen(false);
              }}
              onDeleted={() => {
                setDeleteOpen(false);
                onBack();
              }}
            />
          )}
          <CampaignSummaryBar outcomes={data.outcomes} />
          <CampaignOutcomeTable
            outcomes={data.outcomes}
            pendingCustomerId={updateOutcome.isPending ? updateOutcome.variables.customerId : null}
            onStatusChange={(customerId, status) => {
              updateOutcome.mutate({ customerId, status });
            }}
          />
        </>
      )}
    </div>
  );
}
